import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Zap, Crown, PlayCircle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { PLANS, priceLabel, type Plan } from "@/data/site";

const PLAN_ICONS: Record<Plan["key"], typeof Sparkles> = {
  monthly: Sparkles,
  yearly: Zap,
  lifetime: Crown,
};
interface EnhancedPricingProps {
  onPurchaseSuccess: () => void;
}
const EnhancedPricing = ({
  onPurchaseSuccess
}: EnhancedPricingProps) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();

  // Start a Stripe Checkout session via the self-hosted edge function.
  // The function maps plan -> price + mode (subscription for monthly/yearly, payment for lifetime).
  const handleCheckout = async (planKey: string) => {
    try {
      setLoading(planKey);
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: planKey }
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast({
        title: "Payment Error",
        description: err?.message || "Could not start checkout. Please try again.",
        variant: "destructive"
      });
      setLoading(null);
    }
  };

  // Prices, periods and feature lists all live in src/data/site.ts now. Four places on
  // the site quoted a membership price and no two of them agreed; there is one array
  // left, and the JSON-LD Offer nodes are generated from the same one.
  const plans = PLANS.map((p) => ({
    ...p,
    price: priceLabel(p),
    icon: PLAN_ICONS[p.key],
  }));
  return <section id="pricing" className="py-24 bg-gradient-to-b from-background via-background/50 to-card/30 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{
        animationDelay: '2s'
      }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12 sm:mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 sm:mb-6">
            Choose Your Plan
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            Working automations, prompts and resources — built from real client work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start max-w-6xl mx-auto">
          {/* Pricing Cards */}
          {plans.map((plan, index) => {
          const IconComponent = plan.icon;
          return <Card key={plan.key} className={`group transition-all duration-700 hover:scale-105 bg-gradient-to-br from-card/80 via-card/60 to-card/40 border-2 backdrop-blur-xl h-auto w-full flex flex-col overflow-hidden shadow-[0_8px_20px_-3px_rgba(0,0,0,0.3),0_4px_12px_-2px_rgba(248,76,0,0.2),inset_0_1px_0_0_rgba(255,255,255,0.1)] hover:shadow-[0_20px_40px_-5px_rgba(248,76,0,0.4),0_10px_25px_-3px_rgba(0,0,0,0.5),inset_0_2px_4px_0_rgba(255,255,255,0.15)] border-border/20 hover:border-primary/30 before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/10 before:via-transparent before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500 before:pointer-events-none after:absolute after:inset-[1px] after:bg-gradient-to-br after:from-background/5 after:via-transparent after:to-background/5 after:rounded-lg after:pointer-events-none ${plan.popular ? 'ring-2 ring-primary/40 shadow-[0_8px_25px_-3px_#f15000] border-primary/50' : ''} animate-fade-in relative active:scale-[0.98] transition-transform`} style={{
            animationDelay: `${index * 150}ms`
          }}>
                  {plan.popular && <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-b-full text-xs sm:text-sm font-bold flex items-center gap-1 sm:gap-2 shadow-xl">
                        <Crown className="w-3 h-3 sm:w-4 sm:h-4" />
                        Most Popular
                      </div>
                    </div>}

                  <CardHeader className="text-center pb-4 flex-shrink-0 pt-8 sm:pt-10 px-4 sm:px-6">
                    <div className="flex justify-center mb-2">
                      <div className="p-2 rounded-xl bg-gradient-primary/20 backdrop-blur-sm">
                        <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-lg sm:text-xl font-bold">{plan.name}</CardTitle>
                    <div className="mt-2">
                      <span className="text-2xl sm:text-3xl font-bold text-foreground">{plan.price}</span>
                      <span className="text-muted-foreground text-sm sm:text-base">{plan.period}</span>
                    </div>
                    <CardDescription className="mt-1 text-xs sm:text-sm px-2">{plan.description}</CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow flex flex-col px-4 sm:px-6">
                    <ul className="space-y-2 mb-6 flex-grow">
                      {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start">
                          <div className="mr-2 mt-0.5 p-0.5 rounded-full bg-primary/20 flex-shrink-0">
                            <Check className="h-3 w-3 text-primary" />
                          </div>
                          <span className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{feature}</span>
                        </li>)}
                    </ul>

                    <Button className="w-full bg-gradient-primary hover:opacity-90 text-white border-0 text-sm sm:text-base" size="lg" disabled={loading === plan.key} onClick={() => handleCheckout(plan.key)}>
                      {loading === plan.key ? "Redirecting…" : "Get Started"}
                    </Button>
                  </CardContent>
                </Card>;
        })}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Members of{" "}
          <a href="https://www.skool.com/ai-automations-by-hector-8106/about" target="_blank" rel="noopener noreferrer" className="underline">
            AI Automations by Hector
          </a>{" "}
          on Skool get the full library included — no purchase needed.
        </p>

        {/* Demo Videos */}
        <div className="mt-12 sm:mt-16">
          <h3 className="text-center text-xl sm:text-2xl font-bold text-foreground mb-6 sm:mb-8">See It In Action</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {/* Demo For Templates */}
            <Card className="group cursor-pointer transition-all duration-500 hover:scale-105 bg-gradient-to-br from-card/80 via-card/60 to-card/40 border-2 backdrop-blur-xl overflow-hidden shadow-[0_8px_20px_-3px_rgba(0,0,0,0.3),0_4px_12px_-2px_rgba(248,76,0,0.2),inset_0_1px_0_0_rgba(255,255,255,0.1)] hover:shadow-[0_20px_40px_-5px_rgba(248,76,0,0.4),0_10px_25px_-3px_rgba(0,0,0,0.5),inset_0_2px_4px_0_rgba(255,255,255,0.15)] border-border/20 hover:border-primary/30 relative" onClick={() => setSelectedVideo('https://www.youtube.com/embed/XnZtfsjg3Cs')}>
              <CardHeader className="text-center pb-2 px-3 sm:px-6">
                <CardTitle className="text-base sm:text-lg font-bold text-foreground">Demo For Templates</CardTitle>
              </CardHeader>
              <div className="relative aspect-video overflow-hidden mx-2 sm:mx-4 mb-2 sm:mb-4 rounded-lg">
                <img src="https://img.youtube.com/vi/XnZtfsjg3Cs/maxresdefault.jpg" alt="Demo For Templates" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="bg-white px-4 sm:px-6 py-2 sm:py-3 group-hover:scale-110 transition-transform duration-300 flex items-center gap-1.5 sm:gap-2 rounded-xl">
                    <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <span className="font-semibold text-zinc-950 text-xs sm:text-base">Watch Demo</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Demo For Beginners */}
            <Card className="group cursor-pointer transition-all duration-500 hover:scale-105 bg-gradient-to-br from-card/80 via-card/60 to-card/40 border-2 backdrop-blur-xl overflow-hidden shadow-[0_8px_20px_-3px_rgba(0,0,0,0.3),0_4px_12px_-2px_rgba(248,76,0,0.2),inset_0_1px_0_0_rgba(255,255,255,0.1)] hover:shadow-[0_20px_40px_-5px_rgba(248,76,0,0.4),0_10px_25px_-3px_rgba(0,0,0,0.5),inset_0_2px_4px_0_rgba(255,255,255,0.15)] border-border/20 hover:border-primary/30 relative" onClick={() => setSelectedVideo('https://www.youtube.com/embed/0wLGagCqgxw')}>
              <CardHeader className="text-center pb-2 px-3 sm:px-6">
                <CardTitle className="text-base sm:text-lg font-bold text-foreground">Demo For Beginners</CardTitle>
              </CardHeader>
              <div className="relative aspect-video overflow-hidden mx-2 sm:mx-4 mb-2 sm:mb-4 rounded-lg">
                <img src="https://img.youtube.com/vi/0wLGagCqgxw/maxresdefault.jpg" alt="Demo For Beginners" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="bg-white px-4 sm:px-6 py-2 sm:py-3 group-hover:scale-110 transition-transform duration-300 flex items-center gap-1.5 sm:gap-2 rounded-xl">
                    <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary bg-white/0" />
                    <span className="font-semibold text-zinc-950 text-xs sm:text-base">Watch Demo</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Video Modal */}
        <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
          <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
            <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
              <button onClick={() => setSelectedVideo(null)} className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-2 transition-colors">
                <X className="w-6 h-6 text-white" />
              </button>
              {selectedVideo && <iframe src={selectedVideo} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Demo Video" />}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </section>;
};
export { EnhancedPricing };
